import { Clubhouse_LeagueType } from "../../enums";
import type { Clubhouse_LeagueV2RosterMemberDto } from "../../types/leagueRoster";
import type { Clubhouse_LeagueV2TeamSummaryDto } from "../../types/leagueTeams";

/** The sizes the board can lay out, from the league schema. */
export const MIN_TEAM_SIZE = 1;
export const MAX_TEAM_SIZE = 4;

/**
 * The team size to assume when `leagueV2Detail.teamSize` is absent.
 *
 * This is a product constraint, not a guess at a missing value: this release of
 * Leagues supports 2-person teams only. Operators are never offered a choice of
 * size, which is why the detail frequently omits the field — absence means "the
 * one size there is", so the board draws rather than refusing to.
 *
 * Scope of the assumption, deliberately narrow:
 *   - It covers ABSENCE ONLY. A size the league actually reports always wins,
 *     so when teams of 3 ship, the board follows the API with no change here.
 *   - A reported size the board can't lay out still gates it (see
 *     `isSupportedTeamSize`) — a wrong slot count marks a half-built team
 *     complete, which is worse than showing nothing.
 *
 * Retire this the moment team size becomes an operator choice: at that point an
 * omitted size is genuinely unknown again, and the fail-safe gate should cover
 * it rather than this constant.
 */
export const DEFAULT_TEAM_SIZE = 2;

/** Whether a value off the wire is a size the board can lay out. */
export function isSupportedTeamSize(size: unknown): size is number {
  return (
    typeof size === "number" &&
    Number.isInteger(size) &&
    size >= MIN_TEAM_SIZE &&
    size <= MAX_TEAM_SIZE
  );
}

/**
 * Effective players-per-team for roster and finalize gates. Matches backend
 * `LeagueV2TeamRules.TryResolveMaxPlayersPerTeam`: Scramble leagues omit
 * `teamSize` on the wire and default to 2; Team leagues require an explicit size.
 */
export function resolveLeagueV2TeamSize(
  leagueType?: Clubhouse_LeagueType,
  reported?: number,
): number | undefined {
  if (isSupportedTeamSize(reported)) {
    return reported;
  }
  if (leagueType === Clubhouse_LeagueType.SCRAMBLE) {
    return DEFAULT_TEAM_SIZE;
  }
  return undefined;
}

/** Team name cap, matched to the API column. */
export const TEAM_NAME_MAX = 100;

/**
 * A team or player identifier, exactly as the API returned it.
 *
 * The DTOs declare these as numbers; the API sends strings like "Qd9bRzV5".
 * Nothing here parses or coerces them — they are passed back untouched, so it
 * does not matter which the server actually uses.
 */
export type BoardId = string | number;

export type BoardPlayer = {
  userId: BoardId;
  name: string;
  /** Rendered beside the name; absent for a player with no handicap yet. */
  handicap?: number;
  initials: string;
};

export type BoardTeam = {
  teamId: BoardId;
  name: string;
  /** Exactly `teamSize` entries; a null is an empty slot. */
  slots: (BoardPlayer | null)[];
  isComplete: boolean;
};

/**
 * Initials for the avatar. Takes the first and last word so "Jordan Blake" and
 * "Jordan van Blake" both read JB, and falls back to the first two characters
 * for a single-word name.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

/**
 * The players this board deals in: active, full-time roster members.
 *
 * Substitutes are deliberately excluded — they are managed entirely from the
 * Roster tab and never appear here, so they must not count toward the odd-roster
 * warning either.
 */
export function fullTimePlayers(
  roster: Clubhouse_LeagueV2RosterMemberDto[] | undefined,
): BoardPlayer[] {
  return (roster ?? []).flatMap((m) => {
    if (m.userId == null) return [];
    if (m.isSubstituteOnly) return [];
    if (m.isActive === false) return [];
    const name = m.playerName?.trim() || "Unknown";
    return [
      {
        userId: m.userId,
        name,
        handicap: m.currentHandicap ?? undefined,
        initials: initialsOf(name),
      },
    ];
  });
}

/** A team player row carries name parts rather than a display name. */
function teamPlayerName(p: {
  firstName?: string;
  lastName?: string;
  username?: string;
}): string {
  const full = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return full || p.username?.trim() || "Unknown";
}

/**
 * Teams in display order, each padded to `teamSize` slots.
 *
 * Player details come from the roster where possible so a handicap shown on a
 * team card matches the one shown in the rail; the team payload carries only
 * name parts.
 */
export function toBoardTeams(
  teams: Clubhouse_LeagueV2TeamSummaryDto[] | undefined,
  players: BoardPlayer[],
  teamSize: number,
): BoardTeam[] {
  const byId = new Map<BoardId, BoardPlayer>(players.map((p) => [p.userId, p]));
  return (teams ?? []).flatMap((t) => {
    if (t.teamId == null) return [];
    const assigned = (t.players ?? []).flatMap((p) => {
      if (p.userId == null) return [];
      const known = byId.get(p.userId);
      if (known) return [known];
      // On a team but no longer a full-time roster member: still shown, so the
      // operator can see who to remove rather than wondering about a gap.
      const name = teamPlayerName(p);
      return [{ userId: p.userId, name, initials: initialsOf(name) }];
    });
    // Never shorter than what the server assigned: if a team somehow holds
    // more players than the league's size, show them all rather than hiding
    // the overflow, which would also put them back in the rail as unassigned.
    const slotCount = Math.max(teamSize, assigned.length);
    const slots: (BoardPlayer | null)[] = Array.from(
      { length: slotCount },
      (_, i) => assigned[i] ?? null,
    );
    return [
      {
        teamId: t.teamId,
        name: t.teamName?.trim() || "Unnamed team",
        slots,
        isComplete: assigned.length >= teamSize,
      },
    ];
  });
}

/** Full-time players not on any team, in roster order. */
export function unassignedPlayers(
  players: BoardPlayer[],
  teams: BoardTeam[],
): BoardPlayer[] {
  const taken = new Set<BoardId>(
    teams.flatMap((t) => t.slots.flatMap((s) => (s ? [s.userId] : []))),
  );
  return players.filter((p) => !taken.has(p.userId));
}

export type BoardStats = {
  rosterCount: number;
  teamCount: number;
  completeTeams: number;
  incompleteTeams: number;
  unassignedCount: number;
  /**
   * Players without a partner: everyone unassigned, plus anyone sitting alone
   * on a team that still has an open slot. This is a count of *players*, which
   * is what the warning banner speaks to ("N players still need a teammate").
   */
  needsPartner: number;
  isOddRoster: boolean;
};

export function boardStats(
  players: BoardPlayer[],
  teams: BoardTeam[],
  teamSize: number,
): BoardStats {
  const unassigned = unassignedPlayers(players, teams);
  const incomplete = teams.filter((t) => !t.isComplete);
  const strandedOnTeams = incomplete.reduce(
    (n, t) => n + t.slots.filter(Boolean).length,
    0,
  );
  return {
    rosterCount: players.length,
    teamCount: teams.length,
    completeTeams: teams.length - incomplete.length,
    incompleteTeams: incomplete.length,
    unassignedCount: unassigned.length,
    needsPartner: unassigned.length + strandedOnTeams,
    isOddRoster: players.length % teamSize !== 0,
  };
}

/** How many team shells a roster needs. Zero players means no shells. */
export function requiredTeamShellCount(
  rosterCount: number,
  teamSize: number,
): number {
  if (rosterCount <= 0 || teamSize <= 0) return 0;
  return Math.ceil(rosterCount / teamSize);
}

export type TeamShellPlan = {
  /** Default names for empty shells that still need to be created. */
  createNames: string[];
  /** Empty shells that are no longer needed. Teams with players are never listed. */
  deleteTeamIds: BoardId[];
};

/**
 * Keep empty shells equal to the roster. Creates the shortfall and drops only
 * unused empty shells. A team that already has a player is left where it is.
 */
export function planTeamShells(
  teams: BoardTeam[],
  rosterCount: number,
  teamSize: number,
): TeamShellPlan {
  const required = requiredTeamShellCount(rosterCount, teamSize);
  const occupied = teams.filter((team) => team.slots.some((slot) => slot));
  const empty = teams.filter((team) => team.slots.every((slot) => !slot));
  const neededEmpty = Math.max(0, required - occupied.length);

  if (empty.length > neededEmpty) {
    return {
      createNames: [],
      deleteTeamIds: empty.slice(neededEmpty).map((team) => team.teamId),
    };
  }

  const growing = [...teams];
  const createNames: string[] = [];
  for (let i = empty.length; i < neededEmpty; i++) {
    const name = nextTeamName(growing);
    createNames.push(name);
    growing.push({
      teamId: `pending-${name}`,
      name,
      slots: [],
      isComplete: false,
    });
  }
  return { createNames, deleteTeamIds: [] };
}

const LOCAL_TEAM_ID = /^local-(\d+)$/;

/** A shell that exists only in the editor until someone is saved onto it. */
export function isLocalTeamId(teamId: BoardId): boolean {
  return LOCAL_TEAM_ID.test(String(teamId));
}

function nextLocalTeamId(teams: BoardTeam[]): string {
  const max = teams.reduce((highest, team) => {
    const match = LOCAL_TEAM_ID.exec(String(team.teamId));
    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);
  return `local-${max + 1}`;
}

/**
 * The teams to draw for this roster. Missing shells are added in memory and
 * spare empty shells are dropped. Nothing here calls the server.
 */
export function applyRosterShells(
  teams: BoardTeam[],
  rosterCount: number,
  teamSize: number,
): BoardTeam[] {
  const plan = planTeamShells(teams, rosterCount, teamSize);
  const removed = new Set(plan.deleteTeamIds.map(String));
  const kept = teams.filter((team) => !removed.has(String(team.teamId)));
  const created: BoardTeam[] = [];
  for (const name of plan.createNames) {
    created.push({
      teamId: nextLocalTeamId([...kept, ...created]),
      name,
      slots: Array.from({ length: Math.max(teamSize, 0) }, () => null),
      isComplete: false,
    });
  }
  return [...kept, ...created];
}

function filledSlots(team: BoardTeam): BoardPlayer[] {
  return team.slots.flatMap((slot) => (slot ? [slot] : []));
}

function withCompleteness(team: BoardTeam, teamSize: number): BoardTeam {
  return { ...team, isComplete: filledSlots(team).length >= teamSize };
}

/** Drop a player from every team. Used before placing them on a new one. */
function withoutPlayer(teams: BoardTeam[], userId: BoardId, teamSize: number): BoardTeam[] {
  return teams.map((team) =>
    withCompleteness(
      {
        ...team,
        slots: team.slots.map((slot) =>
          slot?.userId === userId ? null : slot,
        ),
      },
      teamSize,
    ),
  );
}

/** Put `player` in the first open slot on `teamId`. No other player moves. */
export function placeOnTeam(
  teams: BoardTeam[],
  teamId: BoardId,
  player: BoardPlayer,
  teamSize: number,
): BoardTeam[] {
  const cleared = withoutPlayer(teams, player.userId, teamSize);
  return cleared.map((team) => {
    if (String(team.teamId) !== String(teamId)) return team;
    const openAt = team.slots.findIndex((slot) => slot == null);
    if (openAt < 0) return team;
    const slots = team.slots.slice();
    slots[openAt] = player;
    return withCompleteness({ ...team, slots }, teamSize);
  });
}

/** Clear one player from one team. Everyone else stays where they are. */
export function takeOffTeam(
  teams: BoardTeam[],
  teamId: BoardId,
  userId: BoardId,
  teamSize: number,
): BoardTeam[] {
  return teams.map((team) => {
    if (String(team.teamId) !== String(teamId)) return team;
    return withCompleteness(
      {
        ...team,
        slots: team.slots.map((slot) =>
          slot?.userId === userId ? null : slot,
        ),
      },
      teamSize,
    );
  });
}

export function renameTeamDraft(
  teams: BoardTeam[],
  teamId: BoardId,
  name: string,
): BoardTeam[] {
  return teams.map((team) =>
    String(team.teamId) === String(teamId) ? { ...team, name } : team,
  );
}

/** Next default team name: the lowest "Team N" not already taken. */
export function nextTeamName(teams: BoardTeam[]): string {
  const taken = new Set(teams.map((t) => t.name.trim().toLowerCase()));
  for (let n = 1; n <= teams.length + 1; n++) {
    const candidate = `Team ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `Team ${teams.length + 1}`;
}

/** Value equality for one slot — `null` is a hole, not a player to compare. */
function slotEqual(a: BoardPlayer | null, b: BoardPlayer | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.userId === b.userId &&
    a.name === b.name &&
    a.handicap === b.handicap &&
    a.initials === b.initials
  );
}

/**
 * Value equality for one team, used to memoize `TeamCard`/`TeamRow` against a
 * model that rebuilds every team object from scratch on each render (see
 * `toBoardTeams`) — reference equality would never bail a card out, even when
 * every field of a team it holds is unchanged.
 */
export function teamEqual(a: BoardTeam, b: BoardTeam): boolean {
  if (a === b) return true;
  if (a.teamId !== b.teamId || a.name !== b.name || a.isComplete !== b.isComplete)
    return false;
  if (a.slots.length !== b.slots.length) return false;
  return a.slots.every((s, i) => slotEqual(s, b.slots[i] ?? null));
}

/** Value equality for one player, used to memoize `DraggableChip`. */
export function playerEqual(a: BoardPlayer, b: BoardPlayer): boolean {
  return a === b || slotEqual(a, b);
}

/**
 * Whether two team lists carry the same ids and names, in the same order —
 * `TeamCard`'s memo comparator uses this for the sibling-name check its
 * rename box runs (`validateTeamName`, below). `teamEqual` alone lets a card
 * bail out of a render where a *different* team was renamed, and a bailed
 * card keeps its previous render's `teams` closure — so without this, that
 * card's own rename box would validate against sibling names that are
 * already stale, missing a duplicate it should catch.
 */
export function teamNamesEqual(a: BoardTeam[], b: BoardTeam[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((t, i) => t.teamId === b[i]!.teamId && t.name === b[i]!.name);
}

/**
 * Validates a renamed team. Duplicates are rejected case-insensitively, since
 * two teams differing only in casing are indistinguishable on the board.
 */
export function validateTeamName(
  name: string,
  teams: BoardTeam[],
  teamId: BoardId,
): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return "Enter a team name.";
  if (trimmed.length > TEAM_NAME_MAX)
    return `Keep it to ${TEAM_NAME_MAX} characters.`;
  const clash = teams.some(
    (t) =>
      t.teamId !== teamId &&
      t.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  return clash ? "That name is already taken." : undefined;
}
